/** Child of DndContext that contains the actual layout and logic for TodayView. */
import { useState, useCallback } from 'react';
import { Box } from '@mui/material';
import {
  useTasksContext,
  TaskUIProvider,
  useTaskMutationsContext,
} from '@/features/tasks';
import { useNextActions } from '@/features/tasks/hooks/list/use-list-next-actions.js';
import useTaskStructure from '@/features/tasks/hooks/mutations/use-task-structure.js';
import useDailyTaskSections from '@/features/tasks/hooks/list/use-list-daily-sections.js';
import useMobileDndReorder from '@/features/tasks/hooks/list/use-list-mobile-dnd.js';
import { useCelebration } from '@/features/tasks/hooks/status/use-task-celebration.js';
import { useFocusContext } from '@/features/tasks/context/FocusContextExport';
import ActivationNoticeBanner from '@/features/today/components/ActivationNoticeBanner';
import FocusHero from '@/features/today/components/FocusHero';
import { useActivationNotice } from '@/features/today/hooks/use-activation-notice';
import TodayAppointmentsPanel from '@/features/today/components/TodayAppointmentsPanel';
import {
  useBreakpoint,
  isBattlefieldBreakpoint,
  isDesktopBreakpoint,
} from '@/hooks/ui/useBreakpoint';
import { OnboardingHintQueueProvider } from '@/features/onboarding/context/OnboardingHintQueueContext';
import CelebrationSnackbar from './CelebrationSnackbar';
import BulkActionBar from './BulkActionBar';
import DayProgressDotsMinimal from './DayProgressDotsMinimal';
import TodayViewPanels from './TodayViewPanels';
import TodayViewSidebar from './TodayViewSidebar';
import { PANEL_GAP } from '@/components/layout/spacingTokens';
import { useTodayViewUiContext } from '@/features/today/hooks/use-today-view-ui-context';
import { useT } from '@/i18n/useT';
import {
  hasActiveTaskFilter,
  buildFocusHeroHandlers,
} from './todayViewContentHelpers';

const MOBILE_PAGE_PADDING = 1.5;

const TodayViewContent = () => {
  const t = useT();
  const currentBreakpoint = useBreakpoint();
  const isBattlefield = isBattlefieldBreakpoint(currentBreakpoint);
  const isDesktop = isDesktopBreakpoint(currentBreakpoint);
  const {
    allTasks,
    filteredTasks,
    user,
    tasksMap,
    projectFilter,
    contextFilter,
  } = useTasksContext();
  const { activeFocusTaskId, focusStartTime, stopFocus } = useFocusContext();
  const { updateTask, handleToggleComplete, handleStartFocus } =
    useTaskMutationsContext();
  const { moveTask } = useTaskStructure();
  const nextActions = useNextActions();

  const {
    todayTasks,
    completedTodayTasks,
    displayOverdueTasks,
    displayTodayTasks,
    displayNextActions,
    displayUpcomingTasks,
    topActionableTaskId,
    heroTask,
  } = useDailyTaskSections({
    filteredTasks,
    nextActions,
    activeFocusTaskId,
    tasksMap,
  });

  const hasActiveFilter = hasActiveTaskFilter(projectFilter, contextFilter);
  const { celebrationOpen, celebrationMessage, handleCloseCelebration } =
    useCelebration(allTasks, activeFocusTaskId, todayTasks, hasActiveFilter);

  useMobileDndReorder({
    todayTasks,
    nextActions,
    allTasks,
    user,
    updateTask,
    moveTask,
  });

  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const isMultiSelectActive = selectedTaskIds.length > 1;

  const onToggleInSelection = useCallback((taskId) => {
    setSelectedTaskIds((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId],
    );
  }, []);

  const onClearSelection = useCallback(() => {
    setSelectedTaskIds([]);
  }, []);

  const uiContextValue = useTodayViewUiContext({
    selectedTaskIds,
    isMultiSelectActive,
    onToggleInSelection,
    topActionableTaskId,
  });

  const { showNotice, completedCount, dismissNotice } = useActivationNotice({
    userId: user?.uid,
    allTasks,
  });

  const focusHeroHandlers = heroTask
    ? buildFocusHeroHandlers({
        user,
        heroTask,
        activeFocusTaskId,
        focusStartTime,
        stopFocus,
        handleToggleComplete,
        handleStartFocus,
      })
    : null;

  return (
    <TaskUIProvider value={uiContextValue}>
      <OnboardingHintQueueProvider enabled={isDesktop}>
      <Box
        sx={{
          p: isBattlefield ? MOBILE_PAGE_PADDING : 3,
          width: '100%',
          display: 'flex',
          flexDirection: isBattlefield ? 'column' : 'row',
          gap: isBattlefield ? 2 : PANEL_GAP,
          alignItems: 'flex-start',
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            width: '100%',
            position: 'relative',
          }}
        >
          {isBattlefield ? <DayProgressDotsMinimal sx={{ mb: 1.5 }} /> : null}

          {showNotice ? (
            <ActivationNoticeBanner
              completedCount={completedCount}
              onDismiss={dismissNotice}
            />
          ) : null}

          {heroTask && focusHeroHandlers ? (
            <FocusHero
              task={heroTask}
              isFocused={activeFocusTaskId === heroTask.id}
              focusStartTime={focusStartTime}
              {...focusHeroHandlers}
            />
          ) : null}

          <TodayAppointmentsPanel />

          <TodayViewPanels
            t={t}
            isBattlefield={isBattlefield}
            uiContextValue={uiContextValue}
            displayOverdueTasks={displayOverdueTasks}
            displayTodayTasks={displayTodayTasks}
            displayNextActions={displayNextActions}
            displayUpcomingTasks={displayUpcomingTasks}
            todayTasks={todayTasks}
            nextActions={nextActions}
            completedTodayTasks={completedTodayTasks}
            allTasksCount={allTasks.length}
            topActionableTaskId={topActionableTaskId}
            tasksMap={tasksMap}
            selectedTaskIds={selectedTaskIds}
            setSelectedTaskIds={setSelectedTaskIds}
          />

          <CelebrationSnackbar
            open={celebrationOpen}
            message={celebrationMessage}
            onClose={handleCloseCelebration}
          />
          {isMultiSelectActive ? (
            <BulkActionBar
              selectedTaskIds={selectedTaskIds}
              onClearSelection={onClearSelection}
              user={user}
            />
          ) : null}
        </Box>

        {!isBattlefield ? (
          <TodayViewSidebar
            userId={user?.uid}
            allTasks={allTasks}
            tasksMap={tasksMap}
          />
        ) : null}
      </Box>
      </OnboardingHintQueueProvider>
    </TaskUIProvider>
  );
};

export default TodayViewContent;
